import get from 'lodash/get'
import { useLocation, Routes, Route, Navigate } from 'react-router-dom'
import { useCurrentUser } from './redux/slices/session'
import Alerts from './components/Alerts'
import NewAlert from './components/Alerts/New'
import EditAlert from './components/Alerts/Edit'
import ShowAlert from './components/Alerts/Show'
import RedirectToMergedFile from './components/Alerts/RedirectToMergedFile'
import BillingOrders from './components/Billing/BillingOrders'
import BillingOrderDetails from './components/Billing/BillingOrders/Details'
import PracticeBillingOrders from './components/Billing/BillingOrders/PracticeBillingOrders'
import NewOfficeAuthorizations from './components/Billing/OfficeAuthorizations/New'
import OfficeCheckAuthorizations from './components/Billing/OfficeAuthorizations'
import OfficeAuthorizationsDetails from './components/Billing/OfficeAuthorizations/Details'
import OfficeAuthorizationsEdit from './components/Billing/OfficeAuthorizations/Edit'
import OfficeAuthorizations from './components/Billing/OfficeAuthorizations/Authorizations'
import OfficeBillingOrders from './components/Billing/OfficeBillingOrders'
import OfficeBillingOrdersList from './components/Billing/OfficeBillingOrders/OfficeBillingOrders'
import OfficeBillingOrdersDetail from './components/Billing/OfficeBillingOrders/Details'
import Cycles from './components/Cycles'
import PracticeCycles from './components/Cycles/PracticeCycles'
import Dashboard from './components/Dashboard'
import DeviceModels from './components/DeviceModels'
import EditDeviceModel from './components/DeviceModels/Edit'
import NewDeviceModel from './components/DeviceModels/New'
import DeviceTypes from './components/DeviceTypes'
import EditDeviceType from './components/DeviceTypes/Edit'
import Layout from './components/Layout'
import MissedTransmissions from './components/MissedTransmissions'
import ShowMissedTransmission from './components/MissedTransmissions/Details'
import EditMissedTransmission from './components/MissedTransmissions/Edit'
import NewMissedTransmission from './components/MissedTransmissions/New'
import PracticeMissedTransmissions from './components/MissedTransmissions/PracticeMissedTransmissions'
import Patients from './components/Patients'
import ShowPatient from './components/Patients/Details'
import PatientDevices from './components/Patients/Devices'
import EditPatient from './components/Patients/Edit'
import NewPatient from './components/Patients/New'
import Practices from './components/Practices'
import EditPractice from './components/Practices/EditPractice'
import NewPractice from './components/Practices/NewPractice'
import PracticeHealthProviders from './components/Practices/PracticeHealthProviders'
import PreAuthorizations from './components/PreAuthorizations'
import PreAuthorizationDetails from './components/PreAuthorizations/Details'
import EditPreAuthorization from './components/PreAuthorizations/EditPreAuthorization'
import NewPreAuthorization from './components/PreAuthorizations/New'
import PracticePreAuthorizations from './components/PreAuthorizations/PracticePreAuthorizations'
import Profile from './components/Profile'
import Reports from './components/Reports'
import PracticeReports from './components/Reports/PracticeReports'
import PendingReports from './components/Reports/PendingReports'
import UnreviewedReports from './components/Reports/UnreviewedReports'
import ReviewedReportsSummary from './components/Reports/ReviewedReportsSummary'
import ShowReport from './components/Reports/ShowReport'
import UploadReports from './components/Reports/Upload'
import EditReport from './components/Reports/EditReport'
import RouterContext from './components/Router/RouterContext'
import Teams from './components/Teams'
import Transmissions from './components/Transmissions'
import ShowTransmission from './components/Transmissions/Show'
import Users from './components/Users'
import EditUser from './components/Users/EditUser'
import NewUser from './components/Users/NewUser'

import routes, { toPath } from './routes'
import PasswordRecovery from './components/Session/PasswordRecovery'
import PasswordReset from './components/Session/PasswordRecovery/PasswordReset'
import AdminRoute from './components/Router/AdminRoute'
import Authenticated from './components/Authenticated'
import Auth0LoginRedirect from './components/Session/Auth0LoginRedirect'
import Logout from './components/Session/Logout'
import Reminders from './components/Reminders'
import PhoneNumbers from './components/WelcomeLetters/PhoneNumbers'
import NewPhoneNumber from './components/WelcomeLetters/PhoneNumbers/new'
import EditPhoneNumber from './components/WelcomeLetters/PhoneNumbers/edit'
import AddOfficeCheck from './components/OfficeChecks/AddOfficeCheck'
import EditOfficeCheck from './components/OfficeChecks/EditOfficeCheck'
import NewOfficeCheck from './components/OfficeChecks/NewOfficeCheck'
import OfficeCheckReports from './components/OfficeChecks/OfficeCheckReports'
import OfficeChecks from './components/OfficeChecks'
import ShowOfficeCheck from './components/OfficeChecks/ShowOfficeCheck'
import Error404 from './components/Error404'
import AutoLogout from './components/AutoLogout'
import LoggedOut from './components/LoggedOut'
import PracticePresetPhrases from './components/Practices/PracticePresetPhrases'
import PracticeLocations from './components/Practices/PracticeLocations'
import PracticeFeatures from './components/Admin/PracticeFeatures'
import FeatureFlags from './components/Admin/FeatureFlags'
import Orders from './components/Orders'
import OrdersTabs from './components/Orders/OrdersTabs'
import Uploads from './components/Transmissions/Uploads/index'
// had to use this way to have both paths:
// practices/uploads and :practiceId/uploads/new
import UploadsNew from './components/Transmissions/Uploads/Upload'

function App() {
  const currentUser = useCurrentUser()
  const isLogged = Boolean(currentUser)
  const location = useLocation()
  const userRole = currentUser?.role

  const isClinician = userRole === 'clinician'
  const isAdmin = userRole === 'admin'

  const { DEV, VITE_APP_DISABLE_AUTO_LOGOUT } = import.meta.env

  const disableAutoLogout = DEV && VITE_APP_DISABLE_AUTO_LOGOUT === 'true'

  const homePath = () => {
    if (isClinician) {
      return toPath.unreviewedReports()
    }

    return get(location, 'state.referrer', toPath.home())
  }

  if (!isLogged) {
    return (
      <Routes>
        <Route path={routes.logout()} element={<Logout />} />
        <Route path={routes.login()} element={<Navigate replace to="/" />} />
        <Route path={routes.password.reset()} element={<PasswordRecovery />} />
        <Route
          path={routes.password.reset.token()}
          element={<PasswordReset />}
        />
        <Route path={routes.authenticated()} element={<Authenticated />} />
        <Route path={toPath.loggedOut()} element={<LoggedOut />} />
        <Route path="/" element={<Auth0LoginRedirect />} />
        <Route path="*" element={<Auth0LoginRedirect />} />
      </Routes>
    )
  }

  return (
    <RouterContext>
      {!disableAutoLogout && <AutoLogout />}

      <Routes>
        <Route element={<Layout />}>
          {/* Routes */}
          <Route
            path={routes.authenticated()}
            element={<Navigate replace to={toPath.home()} />}
          />
          <Route
            path={routes.login()}
            element={<Navigate replace to={toPath.home()} />}
          />

          <Route path={routes.alerts()} element={<Alerts />} />
          <Route path={routes.alerts.alert.edit()} element={<EditAlert />} />
          <Route
            path={routes.alerts.redirectToMergedFile()}
            element={<RedirectToMergedFile />}
          />
          <Route path={routes.alerts.alert()} element={<ShowAlert />} />
          <Route
            path={routes.preAuthorizations()}
            element={<PreAuthorizations />}
          />
          <Route
            path={routes.preAuthorizations.preAuthorization.edit()}
            element={<EditPreAuthorization />}
          />
          <Route
            path={routes.practices.preAuthorizations()}
            element={<PracticePreAuthorizations />}
          />
          <Route
            path={routes.practices.officeAuthorizations.new()}
            element={<NewOfficeAuthorizations />}
          />
          <Route
            path={routes.practices.officeAuthorizations()}
            element={<OfficeAuthorizations />}
          />
          <Route
            path={routes.remoteChecks.remoteCheck.edit()}
            element={<EditReport isV2Report />}
          />
          <Route path={routes.reports.report.edit()} element={<EditReport />} />
          <Route path={routes.patients()} element={<Patients />} />
          <Route path={routes.patients.new()} element={<NewPatient />} />
          <Route
            path={routes.patients.patient.edit()}
            element={<EditPatient />}
          />
          <Route path={routes.patients.patient()} element={<ShowPatient />} />
          <Route
            path={routes.billing.remoteBillingOrders.remoteBillingOrder()}
            element={<BillingOrderDetails />}
          />
          <Route
            path={routes.billing.remoteBillingOrders()}
            element={<BillingOrders />}
          />
          <Route
            path={routes.billing.officeAuthorizations.officeAuthorization.edit()}
            component={<OfficeAuthorizationsEdit />}
          />
          <Route
            path={routes.billing.officeAuthorizations.officeAuthorization()}
            element={<OfficeAuthorizationsDetails />}
          />
          <Route
            path={routes.billing.officeAuthorizations()}
            element={<OfficeCheckAuthorizations />}
          />
          <Route
            path={routes.billing.officeBillingOrders.officeBillingOrder()}
            element={<OfficeBillingOrdersDetail />}
          />
          <Route
            path={routes.billing.officeBillingOrders()}
            element={<OfficeBillingOrders />}
          />
          <Route
            path={routes.practices.billingOrders()}
            element={<PracticeBillingOrders />}
          />
          <Route
            path={routes.practices.officeBillingOrders()}
            element={<OfficeBillingOrdersList />}
          />
          <Route
            path={routes.practices.healthProviders()}
            element={<PracticeHealthProviders />}
          />
          <Route
            path={routes.cycles.cycle.preAuthorizations.new()}
            element={<NewPreAuthorization />}
          />
          <Route path={routes.cycles()} element={<Cycles />} />
          <Route
            path={routes.practices.officeChecks.new()}
            element={<NewOfficeCheck />}
          />
          <Route
            path={routes.practices.officeChecks.officeCheck.edit()}
            element={<EditOfficeCheck />}
          />
          <Route
            path={routes.practices.officeChecks.officeCheck()}
            element={<ShowOfficeCheck />}
          />
          <Route path={routes.officeChecks()} element={<OfficeChecks />} />
          <Route
            path={routes.practices.officeChecks()}
            element={<OfficeCheckReports />}
          />
          <Route
            path={routes.cycles.cycle.missedTransmissions.new()}
            element={<NewMissedTransmission />}
          />
          <Route
            path={routes.practices.cycles()}
            element={<PracticeCycles />}
          />
          <Route path={routes.reports()} element={<Reports />} />
          <Route
            path={routes.reviewedSummary()}
            element={<ReviewedReportsSummary />}
          />
          <Route
            path={routes.remoteChecks.remoteCheck()}
            element={<ShowReport isV2Report />}
          />
          <Route path={routes.reports.report()} element={<ShowReport />} />

          <Route
            path={routes.practices.reports()}
            element={<PracticeReports />}
          />
          <Route
            path={routes.practices.ordersTabs()}
            element={<OrdersTabs />}
          />
          <Route
            path={routes.preAuthorizations.preAuthorization()}
            element={<PreAuthorizationDetails />}
          />
          <Route
            path={routes.missedTransmissions()}
            element={<MissedTransmissions />}
          />
          <Route
            path={routes.practices.missedTransmissions()}
            element={<PracticeMissedTransmissions />}
          />
          <Route
            path={routes.officeChecks.new()}
            element={
              isClinician || isAdmin ? (
                <AddOfficeCheck />
              ) : (
                <Navigate
                  to={
                    currentUser.practice?.id
                      ? toPath.practices.officeChecks.new({
                          practiceId: currentUser.practice.id,
                        })
                      : homePath()
                  }
                />
              )
            }
          />
          <Route
            path={routes.unreviewedReports()}
            element={
              isClinician || isAdmin ? (
                <UnreviewedReports />
              ) : (
                <Navigate to={toPath.home()} />
              )
            }
          />
          <Route
            path={routes.missedTransmissions.missedTransmission.edit()}
            element={<EditMissedTransmission />}
          />
          <Route
            path={routes.missedTransmissions.missedTransmission()}
            element={<ShowMissedTransmission />}
          />
          <Route path={routes.profile()} element={<Profile />} />
          <Route
            path={routes.patients.patient.devices()}
            element={<PatientDevices />}
          />
          <Route
            path={routes.practices.locations()}
            element={<PracticeLocations />}
          />
          <Route
            path={routes.practices.presetPhrases()}
            element={<PracticePresetPhrases />}
          />
          <Route
            path={routes.practices.healthProviders()}
            element={<PracticeHealthProviders />}
          />
          <Route path={routes.transmissions()} element={<Transmissions />} />
          <Route
            path={routes.transmissions.transmission()}
            element={<ShowTransmission />}
          />
          <Route path={routes.orders()} element={<Orders />} />
          <Route path={routes.practices.uploads()} element={<Uploads />} />
          <Route path={routes.practices.uploadNew()} element={<UploadsNew />} />

          <Route
            path={routes.home()}
            element={
              isClinician ? (
                <Navigate to={homePath()} />
              ) : (
                <Dashboard />
              )
            }
          />

          {/* Admin Routes */}
          <Route element={<AdminRoute />}>
            <Route path={routes.alerts.new()} element={<NewAlert />} />
            <Route path={routes.deviceModels()} element={<DeviceModels />} />
            <Route
              path={routes.deviceModels.new()}
              element={<NewDeviceModel />}
            />
            <Route
              path={routes.deviceModels.deviceModel.edit()}
              element={<EditDeviceModel />}
            />
            <Route path={routes.deviceTypes()} element={<DeviceTypes />} />
            <Route
              path={routes.deviceTypes.deviceType.edit()}
              element={<EditDeviceType />}
            />
            <Route path={routes.practices()} element={<Practices />} />
            <Route path={routes.practices.new()} element={<NewPractice />} />
            <Route
              path={routes.practices.practice.edit()}
              element={<EditPractice />}
            />
            <Route path={routes.teams()} element={<Teams />} />
            <Route path={routes.users()} element={<Users />} />
            <Route path={routes.users.new()} element={<NewUser />} />
            <Route path={routes.users.user.edit()} element={<EditUser />} />
            <Route
              path={routes.cycles.cycle.reports.upload()}
              element={<UploadReports />}
            />
            <Route
              path={routes.pendingReports()}
              element={<PendingReports />}
            />
            <Route
              path={routes.welcomeLetters.phoneNumbers()}
              element={<PhoneNumbers />}
            />
            <Route
              path={routes.welcomeLetters.phoneNumbers.new()}
              element={<NewPhoneNumber />}
            />
            <Route
              path={routes.welcomeLetters.phoneNumbers.phoneNumber.edit()}
              element={<EditPhoneNumber />}
            />
            <Route path={routes.reminders()} element={<Reminders />} />
            <Route
              path={routes.admin.featureFlags()}
              element={<FeatureFlags />}
            />
            <Route
              path={routes.admin.practiceFeatures()}
              element={<PracticeFeatures />}
            />
          </Route>
        </Route>
        <Route path="/*" element={<Error404 />} />
      </Routes>
    </RouterContext>
  )
}

export default App
